import { useQuery } from '@tanstack/react-query';
import * as productsApi from '@/api/products';
import type { ProductListQuery } from '@/api/products';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (query?: ProductListQuery) => [...productKeys.lists(), query] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
  bySlug: (slug: string) => [...productKeys.all, 'slug', slug] as const,
  variants: (id: string) => [...productKeys.detail(id), 'variants'] as const,
  pricing: (id: string) => [...productKeys.detail(id), 'pricing'] as const,
  categories: () => [...productKeys.all, 'categories'] as const,
  sizes: () => [...productKeys.all, 'sizes'] as const,
  manufacturers: () => [...productKeys.all, 'manufacturers'] as const,
  decorationMethods: () => [...productKeys.all, 'decorationMethods'] as const,
};

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to fetch all products with optional filtering
 */
export function useProducts(query?: ProductListQuery) {
  return useQuery({
    queryKey: productKeys.list(query),
    queryFn: () => productsApi.getProducts(query),
  });
}

/**
 * Hook to fetch a single product by ID
 */
export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: productKeys.detail(id!),
    queryFn: () => productsApi.getProduct(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch a product by slug
 */
export function useProductBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: productKeys.bySlug(slug!),
    queryFn: () => productsApi.getProductBySlug(slug!),
    enabled: !!slug,
  });
}

/**
 * Hook to fetch product variants
 */
export function useProductVariants(productId: string | undefined) {
  return useQuery({
    queryKey: productKeys.variants(productId!),
    queryFn: () => productsApi.getProductVariants(productId!),
    enabled: !!productId,
  });
}

/**
 * Hook to fetch customer-specific pricing
 */
export function useCustomerPricing(productId: string | undefined) {
  return useQuery({
    queryKey: productKeys.pricing(productId!),
    queryFn: () => productsApi.getCustomerPricing(productId!),
    enabled: !!productId,
  });
}

/**
 * Hook to fetch all product categories
 */
export function useCategories() {
  return useQuery({
    queryKey: productKeys.categories(),
    queryFn: () => productsApi.getCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes - categories don't change often
  });
}

/**
 * Hook to fetch all product sizes
 */
export function useSizes() {
  return useQuery({
    queryKey: productKeys.sizes(),
    queryFn: () => productsApi.getSizes(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch all manufacturers
 */
export function useManufacturers() {
  return useQuery({
    queryKey: productKeys.manufacturers(),
    queryFn: () => productsApi.getManufacturers(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch all decoration methods
 */
export function useDecorationMethods() {
  return useQuery({
    queryKey: productKeys.decorationMethods(),
    queryFn: () => productsApi.getDecorationMethods(),
    staleTime: 10 * 60 * 1000,
  });
}
